import React, { Component } from 'react';
import { Helmet } from 'react-helmet';
import { connect } from 'react-redux';
import {
  Link, Switch, Route, withRouter, Prompt,
} from 'react-router-dom';
import { withTranslation } from 'react-i18next';
import {
  Container, Grid, Button, Divider,
} from 'semantic-ui-react';
import toast from 'react-hot-toast';
import api from 'api';
import util from 'utils/utils';
import actions from 'actions';
import withTheme from 'src/theme/withTheme';

import { SideNavLink } from 'components/lib/SideNav';
import { Sticky, RoundButton } from 'components/lib/UI';

import AccountSettings from './Account';
import NotificationsSettings from './Notifications';
import CommunicationSettings from './Communication';
import PasswordSetings from './Password';
import ProfileSettings from './Profile';
import ApiSettings from './ApiSettings';

class UserSettings extends Component {
  constructor(props) {
    super(props);
    this.state = {
      user: {},
      unsaved: false,
      loading: false,
      saving: false,
    };
  }

  componentDidMount() {
    this.setState({ loading: true, errorMessage: '' });
    api.users.get(this.props.user._id, (user) => this.setState({ user, loading: false }), (err) => this.setState({ errorMessage: err.message, loading: false }));
  }

  setUnsaved = () => {
    if (!this.state.unsaved) this.setState({ unsaved: true });
  };

  handleContextRef = (contextRef) => this.setState({ contextRef });

  updatePassword = () => {
    this.setState({ working: true });
    api.auth.updatePassword(this.state.currentPassword, this.state.newPassword, () => {
      toast.success(this.props.t('users.account.options.changePassword.success'));
      this.setState({ currentPassword: '', newPassword: '', working: false });
    }, (err) => {
      toast.error(err.message);
      this.setState({ working: false });
    });
  };

  updateProfile = (label, value) => {
    this.setUnsaved();
    const user = { ...this.state.user };
    const validFields = ['github', 'linkedin', 'instagram', 'twitter', 'facebook'];
    if ((validFields.includes(label)) && util.validateUrl(value)) {
      const urlPath = util.getUrlPath(value);
      if (!urlPath) {
        user.profile[label] = value;
      } else {
        // Split path, and remove any empty values, which would be a result of trailing/preceding slashes
        // Then take final value, to get the account id
        const splitPath = urlPath.split('/').filter((part) => part !== '');
        user.profile[label] = splitPath[splitPath.length - 1];
      }
    } else if (validFields.includes(label)) {
      // Split if issue accidentally includes the entire path. For example, they may try and do "/in/xniall" for linkedin
      const splitValue = value.split('/').filter((part) => part !== '');
      if (splitValue.length > 1) {
        user.profile[label] = splitValue[splitValue.length - 1];
      } else {
        user.profile[label] = value;
      }
    } else {
      user.profile[label] = value;
    }
    this.setState({ user });
  };

  toggleTag = (tag, checked) => {
    const { user } = this.state;
    const { fullTags = [] } = user;
    const { onUpdateTags } = this.props;

    if (!checked) {
      api.tags.untagSingle(user.organisation, 'users', tag._id, user._id, (newTag) => {
        const updatedTags = Object.assign([], fullTags);
        updatedTags.splice(fullTags.findIndex((ft) => ft._id === newTag._id), 1);
        onUpdateTags(updatedTags);
        this.setState({ user: { ...user, tags: updatedTags.map((t) => t._id), fullTags: updatedTags } });
      });
    } else {
      api.tags.tagSingle(user.organisation, 'users', tag._id, user._id, (newTag) => {
        const updatedTags = Object.assign([], fullTags);
        updatedTags.push(newTag);
        onUpdateTags(updatedTags);
        this.setState({ user: { ...user, tags: updatedTags.map((t) => t._id), fullTags: updatedTags } });
      }, (err) => toast.error(err.message));
    }
  };

  saveProfile = () => {
    this.setState({ saving: true });
    const profile = this.state.user && this.state.user.profile;
    api.users.update(this.props.user._id, { profile }, (user) => {
      toast.success(this.props.t('generic.updated', { type: 'Profile' }));
      this.setState({ user, saving: false, unsaved: false });
      this.props.onProfileUpdated(user.profile);
    }, (err) => {
      toast.error(err.message);
      this.setState({ saving: false });
    });
  };

  render() {
    const { t } = this.props;
    const { user, saving, unsaved } = this.state;
    const path = window.location.pathname;
    return (
      <Container style={{ marginTop: 20, marginBottom: 20 }}>
        <Helmet title={'Settings'} />
        <Prompt
          when={unsaved}
          message={(location) => (location.pathname.match(/\/preferences([A-z0-9])*\/?([A-z0-9])*\/?$/gi) ? true : 'Your profile changes are unsaved. Really leave this page?')}
        />
        <div>
          <Button secondary basic floated='right' icon='arrow left' content={t('users.settings.viewProfile')} as={Link} to={`/users/${user._id}`} />
        </div>
        <Divider clearing hidden />

        <div ref={this.handleContextRef}>
          <Grid stackable>
            <Grid.Column computer={4} >
              <Sticky top={60}>
                <h2 style={{ marginTop: 0 }}>{t('generic.settings')}</h2>
                <SideNavLink active={path === '/preferences'} to='/preferences' iconLeft='settings' content={t('users.settings.sections.account')} />
                <SideNavLink active={path === '/preferences/profile'} to='/preferences/profile' iconLeft='address card outline' content={t('users.settings.sections.profile')} />
                {!this.props.user?.organisation?.preventEmailChange
                  && <SideNavLink active={path === '/preferences/email' || path === '/preferences/communication'} to='/preferences/communication' iconLeft='at' content='Communication' />
                }
                {!this.props.user?.ssoUser && <SideNavLink active={path === '/preferences/password'} to='/preferences/password' iconLeft='lock' content={t('users.settings.sections.password')} />}
                <SideNavLink active={path === '/preferences/notifications'} to='/preferences/notifications' iconLeft='bell' content={t('users.settings.sections.notifications')} />
                <SideNavLink active={path === '/preferences/connections'} to='/preferences/connections' iconLeft='plug' content={t('users.settings.sections.api')} />
                <Grid >
                  <Grid.Row only='mobile'><div style={{
                    borderBottom: 'solid grey 1px', minWidth: '100%', minHeight: '20px', marginBottom: '5px',
                  }}></div></Grid.Row>
                </Grid>
                {(path === '/preferences/profile')
                  && <React.Fragment>
                    <Divider hidden />
                    {unsaved && <RoundButton style={{ marginBottom: '5px' }} loading={saving} fluid primary icon='save' content={t('generic.saveChanges')} onClick={this.saveProfile} />}
                  </React.Fragment>
                }

              </Sticky>
            </Grid.Column>
            <Grid.Column computer={12}>
              <Switch>
                <Route exact path='/preferences' component={AccountSettings} />
                <Route path='/preferences/profile' render={(routeProps) => <ProfileSettings {...routeProps} {...this.props} saveProfile={this.saveProfile} toggleTag={this.toggleTag} updateProfile={this.updateProfile} profileUser={user} saving={saving} />
                } />
                {!this.props.user?.organisation?.preventEmailChange && <Route path='/preferences/(email|communication)' component={CommunicationSettings} />}
                {!this.props.user?.ssoUser && < Route path='/preferences/password' component={PasswordSetings} />}
                <Route path='/preferences/notifications' component={NotificationsSettings} />
                <Route path='/preferences/connections' component={ApiSettings} />
              </Switch>
            </Grid.Column>
          </Grid>
        </div>
      </Container>
    );
  }
}

const mapStateToProps = (state) => ({ user: state.user });
const mapDispatchToProps = (dispatch) => ({
  onProfileUpdated: (profile) => dispatch(actions.user.updateProfile(profile)),
  onUpdateTags: (tags) => dispatch(actions.user.updateTags(tags)),
});

const UserSettingsContainer = withTranslation()(withRouter(connect(
  mapStateToProps, mapDispatchToProps,
)(UserSettings)));

export default withTheme(UserSettingsContainer);
